import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import React from 'react'
import { SetURLSearchParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { type CommonError } from 'src/api'
import { errorsToArray, truncateString } from 'src/helpers/fns'
import { APIResponse, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import ErrorIcon from 'src/imgs/error_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly search: string
  readonly server: string
}
export default function AdministratorCachesPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('caches:cache_keys')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function calculateIndex(pagination: Api.Pagination, index: number) {
  return (pagination.currentPage - 1) * pagination.perPage + index + 1
}
const perPageOptions = [
  { value: 10, label: 10 },
  { value: 20, label: 20 },
  { value: 50, label: 50 },
  { value: 100, label: 100 },
]
function PageContent() {
  const t = useTranslatable()
  const locale = useLocale()
  const breadcrumbsItems = [{ page: `${t('caches:cache_keys')}`, path: `/${locale}/administrator/caches` }]
  const [searchParams, setSearchParams] = useSearchParams({
    search: '',
    server: '',
    page: '1',
    perPage: '10',
  })
  const search = searchParams.get('search') || ''
  const server = searchParams.get('server') || ''
  const cacheKey = searchParams.get('cacheKey') || ''
  const initialValues: FormikValues = { search, server }
  const headers = useAuthenticatedHeaders()
  const [isFlushing, setIsFlushing] = React.useState(false)
  const page = searchParams.get('page')!
  const perPage = searchParams.get('perPage')!

  const caches = useApi({
    endpoint: Api.getAdministratorCaches,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          filters: {
            search: search!,
            server: server!,
          },
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
        },
      }),
      [headers, search, server, page, perPage]
    ),
    suspense: false,
  })

  const flushCache = React.useCallback(async () => {
    setIsFlushing(true)
    try {
      await Api.flushEntireCaches({
        headers,
      })
      await caches.mutate()
      toast.success(t('caches:cache_successfully_flushed'))
    } catch (error) {
      toast.error(t('caches:cache_not_flushed'))
    } finally {
      setIsFlushing(false)
    }
  }, [headers, t, caches])

  return (
    <Section
      title={t('caches:caches')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faDatabase} />}
      rightElement={
        <Button variant="red" onClick={flushCache} disabled={isFlushing} className="flex items-center gap-2">
          {isFlushing && <Loader width="30px" />}
          {t('caches:flush_entire_caches')}
        </Button>
      }
    >
      <Form
        initialValues={initialValues}
        onSubmit={async (values: FormikValues) => {
          setSearchParams((params) => {
            Object.entries(values).forEach(([key, value]) => {
              params.set(key, value)
            })

            return params
          })
        }}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch isFlushing={isFlushing} />
      </Form>

      <TableContent caches={caches} setSearchParams={setSearchParams} isFlushing={isFlushing} />
      <ContentModal cacheKey={cacheKey} setSearchParams={setSearchParams} />
    </Section>
  )
}

interface tableContentProps {
  caches: APIResponse<Api.getAdministratorCachesOk | null, unknown>
  setSearchParams: SetURLSearchParams
  isFlushing: boolean
}

function TableContent({ caches, setSearchParams, isFlushing }: tableContentProps): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const cachesData = caches.data?.data
  const deleteCache = React.useCallback(
    async (key: string) => {
      try {
        const encoded = key ? btoa(key) : ''
        await Api.deleteAdministratorCache({
          headers,
          query: {
            key: encoded,
          },
        })
        void caches.mutate()
        toast.success(t('caches:cache_successfully_deleted'))
      } catch (error) {
        toast.error(t('caches:cache_not_deleted'))
      }
    },
    [caches, headers, t]
  )

  const setCacheKeyParam = React.useCallback(
    (cacheKey: string | undefined) => {
      if (!cacheKey) return
      setSearchParams((params) => {
        params.delete('cacheKey')
        params.set('cacheKey', cacheKey)
        return params
      })
    },
    [setSearchParams]
  )

  if (caches.isLoading || caches.isValidating) {
    return <Loader className="flex" style={{ margin: 'auto' }} />
  }

  if (caches.error != null) {
    const error = caches.error as any as CommonError
    return (
      <NoContent
        header={t('error:an_error_occurred')}
        image={ErrorIcon}
        errors={error.type === 'ErrorsObject' ? errorsToArray(error.errors) : []}
      />
    )
  }
  if (cachesData == null || cachesData.length === 0 || caches.data?.meta == null) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }
  return (
    <div className="mb-7">
      <div className="mt-4 flex flex-col justify-between">
        <span className="text-xs !text-captionColor">
          {t('caches:total_keys')}: {caches.data.meta.pagination?.total}
        </span>
      </div>
      <PaginationTop pagination={caches.data?.meta.pagination} options={{ perPageOptions }} />
      <Table.Table className="mt-4">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="!pl-6">{t('common:unique')}</Table.Th>
            <Table.Th>{t('caches:key')}</Table.Th>
            <Table.Th>{t('caches:expires')}</Table.Th>
            <Table.Th>{t('caches:server')}</Table.Th>
            <Table.Th>{t('common:actions')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {cachesData?.map((cache, index) => (
            <Table.Tr key={index}>
              <Table.Td className="!pl-6">{calculateIndex(caches.data!.meta.pagination, index)}</Table.Td>
              <Table.Td>{truncateString(cache.key, 40)}</Table.Td>
              <Table.Td>
                <div className="flex flex-col gap-1">
                  {cache.expires_at && <span className="whitespace-nowrap">{cache.expires_at}</span>}
                  {cache.expires_in && (
                    <span className="text-xs text-captionColor">
                      {t('caches:expires_in')}: <span className="font-medium">{cache.expires_in}</span>
                    </span>
                  )}
                </div>
              </Table.Td>
              <Table.Td>{cache.server}</Table.Td>
              <Table.Td>
                <div className="flex max-w-[210px] flex-wrap justify-between gap-2">
                  <Button
                    variant="blue"
                    className="w-[100px] text-center text-primaryTextColor"
                    onClick={() => setCacheKeyParam(cache.key)}
                    type="button"
                    disabled={isFlushing}
                  >
                    {t('common:content')}
                  </Button>
                  <DeleteCaheButton deleteCache={() => deleteCache(cache.key!)} isFlushing={isFlushing} />
                </div>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={caches.data?.meta.pagination} options={{ perPageOptions }} />
    </div>
  )
}

interface formSearchProps {
  isFlushing: boolean
}
function FormSearch({ isFlushing }: formSearchProps): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [searchParams, setSearchParams] = useSearchParams()
  const server = searchParams.get('server') || ''
  const search = searchParams.get('search') || ''
  const { data: servers, isValidating: isServersLoading } = useApi({
    endpoint: Api.getAdministratorCacheServers,
    params: React.useMemo(
      () => ({
        headers,
        suspense: false,
      }),
      [headers]
    ),
  })

  const serverOptions = React.useMemo(() => {
    if (!servers) return []
    return [
      { value: '', label: t('common:any') },
      ...servers.data.attributes.map((server) => ({
        value: server || '',
        label: server,
      })),
    ]
  }, [servers, t])
  const isDirty = formik.values.search !== search || formik.values.server !== server
  const canCancel = search === '' && server === ''
  return (
    <fieldset disabled={formik.isSubmitting || isFlushing} className="flex w-full flex-col justify-between">
      <FormError />
      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <TextInput
            type="text"
            label={t('common:search_for_keywords')}
            name="search"
            placeholder={t('common:search')}
            disabled={isFlushing}
          />
        </div>
        <div className="w-full min-w-[100px] md:w-[49%]">
          <SelectInput
            label={t('caches:servers')}
            name="server"
            options={serverOptions}
            disabled={formik.isSubmitting || isServersLoading || isFlushing}
            isLoading={isServersLoading}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty || isFlushing}
          type="submit"
          onClick={() => {
            formik.submitForm()
          }}
        >
          {t('common:search')}
        </Button>
        <Button
          variant="yellow"
          onClick={() => {
            setSearchParams({
              search: '',
              server: '',
            })
            formik.resetForm()
            formik.submitForm()
          }}
          type="reset"
          disabled={formik.isSubmitting || canCancel || isFlushing}
          className="ml-2"
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

interface contentModalProps {
  cacheKey: string
  setSearchParams: SetURLSearchParams
}

function ContentModal({ cacheKey, setSearchParams }: contentModalProps): React.ReactElement | null {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const onCloseModal = React.useCallback(() => {
    setSearchParams((params) => {
      params.delete('cacheKey')
      return params
    })
  }, [setSearchParams])
  const encoded = cacheKey ? btoa(cacheKey) : ''

  const {
    data: cache,
    isValidating: isCacheLoading,
    error: cacheError,
  } = useApi({
    endpoint: Api.getAdministratorCacheContent,
    params: React.useMemo(
      () => ({
        headers,
        query: { key: encoded },
      }),
      [headers, encoded]
    ),
    shouldFetch: Boolean(cacheKey),
    suspense: false,
  })
  const error = cacheError as any as CommonError

  const formattedJson = React.useMemo(() => {
    if (!cache?.data) return null
    try {
      const parsed = JSON.parse(cache.data)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return cache.data
    }
  }, [cache])

  return (
    <Modal
      isModalOpen={Boolean(cacheKey)}
      onClose={onCloseModal}
      title={`${t('caches:cache')}: ${cacheKey}`}
      footer={
        <Button variant="red" onClick={onCloseModal}>
          {t('common:close')}
        </Button>
      }
    >
      <div className="my-4">
        {error != null ? (
          <NoContent
            header={t('error:an_error_occurred')}
            image={ErrorIcon}
            errors={error.type === 'ErrorsObject' ? errorsToArray(error.errors) : []}
          />
        ) : isCacheLoading ? (
          <Loader className="flex" style={{ margin: 'auto' }} />
        ) : formattedJson != null ? (
          <div className="max-h-[70vh] overflow-auto rounded-md border border-borderGrey bg-card p-4">
            <pre className="m-0 whitespace-pre-wrap break-words font-mono text-sm text-primaryTextColor">
              <code>{formattedJson}</code>
            </pre>
          </div>
        ) : (
          <NoContent header={t('error:records_not_found')} image={Warning} />
        )}
      </div>
    </Modal>
  )
}

interface DeleteCaheButtonProps {
  deleteCache: () => Promise<void>
  isFlushing: boolean
}

function DeleteCaheButton({ deleteCache, isFlushing }: DeleteCaheButtonProps): React.ReactElement | null {
  const t = useTranslatable()
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleDeleteCache = React.useCallback(async () => {
    setIsDeleting(true)
    await deleteCache()
    setIsDeleting(false)
  }, [deleteCache, setIsDeleting])
  return (
    <Button
      variant="red"
      className="flex w-[100px] items-center justify-center text-center text-primaryTextColor"
      disabled={isDeleting || isFlushing}
      onClick={handleDeleteCache}
      type="button"
    >
      {isDeleting && <Loader width="30px" />}
      {t('common:delete')}
    </Button>
  )
}
